import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CoinPricePoint, publicMarketService } from '../../services/publicMarketService';
import { store } from '../../store';

function formatChartPrice(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!v || !Number.isFinite(v)) return '—';
  if (v >= 1000) {
    return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(4)}`;
  if (v >= 0.0001) return `$${v.toFixed(6)}`;
  return `$${v.toFixed(8)}`;
}

function resolveFetchMode(): 'admin' | 'retail' | null {
  const { auth } = store.getState();
  if (auth.adminUser && auth.token) return 'admin';
  if (auth.isAuthenticated && !auth.adminUser && (auth.token || auth.type === 'SESSION')) return 'retail';
  return null;
}

interface CoinActivityChartProps {
  symbol: string;
  change24h: number;
  isAdminContext: boolean;
  hours?: number;
}

const CoinActivityChart: React.FC<CoinActivityChartProps> = ({
  symbol,
  change24h,
  isAdminContext,
  hours = 24,
}) => {
  const [points, setPoints] = useState<CoinPricePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPoints([]);

    const mode = isAdminContext ? 'admin' : resolveFetchMode();
    const load =
      mode === 'admin'
        ? publicMarketService.getAdminCoinChart(symbol, hours)
        : mode === 'retail'
          ? publicMarketService.getRetailCoinChart(symbol, hours)
          : Promise.resolve([] as CoinPricePoint[]);

    load
      .then(data => {
        if (cancelled) return;
        if (!data || data.length === 0) {
          setError('No price history available for this symbol.');
        } else {
          setPoints(data);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load price chart.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [symbol, hours, isAdminContext]);

  const { high, low } = useMemo(() => {
    if (points.length === 0) return { high: null as number | null, low: null as number | null };
    let hi = points[0].price;
    let lo = points[0].price;
    for (const p of points) {
      if (p.price > hi) hi = p.price;
      if (p.price < lo) lo = p.price;
    }
    return { high: hi, low: lo };
  }, [points]);

  const stroke = change24h >= 0 ? '#0dca9d' : '#ff7171';
  const gradientId = `coin-activity-${symbol.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <div
      style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '12px 10px 8px',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 8, paddingLeft: 4, paddingRight: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
          {hours}h price activity
        </div>
        {!loading && !error && high != null && low != null && (
          <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right' }}>
            H {formatChartPrice(high)} · L {formatChartPrice(low)}
          </div>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
          Loading chart…
        </div>
      )}

      {!loading && error && (
        <div style={{ textAlign: 'center', padding: '24px 12px', color: '#94a3b8', fontSize: 12 }}>
          {error}
        </div>
      )}

      {!loading && !error && points.length > 0 && (
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={stroke} stopOpacity={0.22} />
                <stop offset="95%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fill: '#94a3b8' }}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 9, fill: '#94a3b8' }}
              width={48}
              domain={['auto', 'auto']}
              tickFormatter={(v: number) => {
                if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
                if (v >= 1) return `$${v.toFixed(2)}`;
                if (v >= 0.01) return `$${v.toFixed(3)}`;
                return `$${v.toFixed(5)}`;
              }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
              formatter={(value: number) => [formatChartPrice(value), 'Price']}
              labelFormatter={(label: string) => label}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke={stroke}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 3 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default CoinActivityChart;
